import { Globe } from 'lucide-react'

import { cn } from '@/lib/cn'
import { findCountry } from '@/lib/countries'

/* The circular country flag — the one visual signature of the shadcn country
 * dropdown this product's pickers were asked to match.
 *
 * WHY AN <img> AND NOT THE EMOJI. Every flag in this codebase used to be the
 * regional-indicator pair (🇰🇿). It is free, it needs no asset pipeline, and on
 * Windows it does not exist: no shipped font carries emoji-flag glyphs, so
 * Chrome and Edge draw a boxed "KZ" instead. This product's users are on
 * Windows. A picker whose whole point is "recognise the country at a glance"
 * cannot be built on a glyph that half the installed base cannot render.
 *
 * WHY LOCAL SVGs AND NOT THE CDN. `react-circle-flags`, which the reference
 * component uses, is an <img> pointed at hatscripts.github.io. This application
 * is one Go binary serving its own assets, deployed where the API is — a flag
 * fetched from GitHub is a flag missing on any network that does not reach it.
 * The same artwork is vendored into `public/flags/` by `npm run flags:sync`
 * (scripts/sync-flags.mjs) and ships inside the binary.
 *
 * DECORATIVE BY DEFAULT. Every place this renders, the country is also named in
 * text beside it. So the image is `alt=""` and the whole element is hidden from
 * assistive technology: a screen reader that announced "Kazakhstan" for the flag
 * and then "Kazakhstan" for the label would read the row twice. Pass a `title`
 * only where the flag genuinely stands alone.
 */

export interface CircleFlagProps {
  /** ISO 3166-1 alpha-2, in any case. Anything else renders the globe. */
  code: string | undefined | null
  /** Pixel diameter. 20 is the reference's own size and the default here. */
  size?: number
  className?: string
  /** Native tooltip. Set it only where no text names the country. */
  title?: string
}

/* Base UI: none — an <img> and a fallback icon. */
export function CircleFlag({ code, size = 20, className, title }: CircleFlagProps) {
  const country = findCountry(code)

  /* No country: a legacy port name, a blank field, one of the codes with no
     artwork. A globe rather than nothing at all, because an empty box would
     collapse the row's leading column and make the list jump as it filters. */
  if (!country) {
    return (
      <span
        aria-hidden
        title={title}
        className={cn(
          'text-text-faint inline-flex shrink-0 items-center justify-center',
          className,
        )}
        style={{ width: size, height: size }}
      >
        <Globe size={Math.round(size * 0.8)} />
      </span>
    )
  }

  return (
    <img
      src={`${import.meta.env.BASE_URL}flags/${country.code.toLowerCase()}.svg`}
      /* Empty alt + aria-hidden: decorative, see the note above. `title` is the
         one channel left open for the rare caller that shows no text. */
      alt=""
      aria-hidden
      title={title}
      width={size}
      height={size}
      loading="lazy"
      /* The artwork is already circular, so the radius only matters while the
         file is loading or if one is ever missing — it keeps the placeholder the
         same shape as the flag and stops the row from reflowing. */
      className={cn('shrink-0 rounded-full object-cover', className)}
      style={{ width: size, height: size }}
    />
  )
}
